import React from 'react';
import { MinerStatus } from '../../types';
import { formatLitecoinHashrate, formatSHA256Hashrate , formatTimeAgo } from '../../utils';

interface MinersPanelProps {
  miners: MinerStatus[];
  loading: boolean;
}

const MinersPanel: React.FC<MinersPanelProps> = ({ miners, loading }) => {
  // Use miners data from props (managed by persistent hook)
  const displayMiners = miners;

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="card p-4 animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gray-200 dark:bg-slate-800 rounded-lg"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-1/3"></div>
                <div className="h-3 bg-gray-200 dark:bg-slate-800 rounded w-1/2"></div>
              </div>
              <div className="w-16 h-8 bg-gray-200 dark:bg-slate-800 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const getStatusColor = (status: MinerStatus['status']) => {
    switch (status) {
      case 'online': return 'bg-emerald-500';
      case 'offline': return 'bg-gray-400 dark:bg-slate-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-400 dark:bg-slate-500';
    }
  };

  // Status is carried by a restrained accent on a shared neutral surface —
  // never a fully tinted card. Offline units recede; error units warn.
  const getStatusBg = (status: MinerStatus['status']) => {
    switch (status) {
      case 'online': return 'border-gray-200 dark:border-slate-800';
      case 'offline': return 'border-gray-200 dark:border-slate-800 opacity-60';
      case 'error': return 'border-red-300 dark:border-red-900';
      default: return 'border-gray-200 dark:border-slate-800 opacity-60';
    }
  };

  // Categorical algorithm colors — fixed assignment, distinct from semantic set
  const getAlgorithmColor = (algorithm: string) => {
    const colors = {
      'SHA256': 'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400',
      'Scrypt': 'bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-400',
      'X11': 'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-400',
      'Ethash': 'bg-slate-100 dark:bg-slate-500/15 text-slate-700 dark:text-slate-300'
    };
    return colors[algorithm as keyof typeof colors] || 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300';
  };

  // Helper function to format hashrate based on algorithm
  const formatMinerHashrate = (hashrate: number, algorithm: string) => {
    if (algorithm === 'Scrypt') {
      // Litecoin miners come in MH/s, show as GH/s
      return formatLitecoinHashrate(hashrate);
    } else if (algorithm === 'SHA256') {
      // SHA256 miners come in TH/s, show as TH/s
      return formatSHA256Hashrate(hashrate);
    } else {
      // Fallback for other algorithms
      return `${hashrate.toFixed(3)} H/s`;
    }
  };

  // Calculate total hashrate properly for different algorithms
  const calculateTotalHashrate = () => {
    // Separate calculations for different algorithms with proper units
    const sha256Miners = displayMiners.filter(m => m.algorithm === 'SHA256');
    const dogecoinMiners = displayMiners.filter(m => m.algorithm === 'Scrypt');

    const sha256TotalMH = sha256Miners.reduce((sum, miner) => sum + miner.hashrate, 0);
    const dogecoinTotalMH = dogecoinMiners.reduce((sum, miner) => sum + miner.hashrate, 0);

    // Convert to display units: Litecoin MH/s -> GH/s, SHA256 MH/s -> GH/s
    const dogecoinGH = dogecoinTotalMH / 1000;
    const sha256GH = sha256TotalMH ;

    // Display both totals in their appropriate units
    if ( sha256Miners.length > 0 && dogecoinMiners.length>0) {
      return ` ${dogecoinGH.toFixed(3)} GH/s`;
    } else if (sha256Miners.length > 0) {
      return `${sha256GH.toFixed(3)} TH/s`;
    }else if (dogecoinMiners.length>0){
      return `${dogecoinGH.toFixed(3)} GH/s`;
    }

    return '0.000 H/s';
  };

  return (
    <div className="space-y-3 short:space-y-2">
      {/* Summary Strip */}
      <div className="card p-4 short:p-3">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <div>
            <p className="stat-label">Fleet</p>
            <p className="flex items-center gap-1.5 text-lg tall:text-xl short:text-base font-bold tabular-nums text-gray-900 dark:text-white leading-tight">
              <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" aria-hidden="true"></span>
              {displayMiners.filter(m => m.status === 'online').length} of {displayMiners.length} online
            </p>
          </div>
          <div className="text-right">
            <p className="stat-label">Total Hashrate</p>
            <p className="text-lg tall:text-xl short:text-base font-bold tabular-nums text-gray-900 dark:text-white leading-tight">
              { calculateTotalHashrate() }
            </p>
          </div>
        </div>
      </div>

      {/* Miners Grid - Responsive Layout */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(17rem,1fr))] gap-3 short:gap-2">
        {displayMiners.map((miner) => (
          <div
            key={miner.id}
            className={`bg-white dark:bg-slate-900 rounded-xl p-4 short:p-3 shadow-sm dark:shadow-none border ${getStatusBg(miner.status)}`}
          >
            <div className="flex items-start space-x-3">
              {/* Status Indicator */}
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 bg-gray-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
                <div className={`absolute -top-1 -right-1 w-3 h-3 ${getStatusColor(miner.status)} rounded-full border-2 border-white dark:border-slate-900`}></div>
              </div>

              {/* Miner Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1.5">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {miner.name}
                  </h4>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getAlgorithmColor(miner.algorithm)}`}>
                    {miner.algorithm}
                  </span>
                </div>

                {/* Hashrate - Primary Readout */}
                <div className="mb-2 short:mb-1">
                  <p className="text-xl tall:text-2xl short:text-lg font-bold tabular-nums text-gray-900 dark:text-white leading-tight">
                    {formatMinerHashrate(miner.hashrate, miner.algorithm)}
                  </p>
                </div>

                {/* Additional Info */}
                <div className="space-y-1 short:space-y-0.5 text-xs text-gray-500 dark:text-slate-400">
                  <div className="flex items-center justify-between">
                    <span>Pool</span>
                    <span className="font-medium text-gray-700 dark:text-slate-300">{miner.pool.replace(' Pool', '')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Difficulty</span>
                    <span className="tabular-nums text-gray-700 dark:text-slate-300">{miner.difficulty.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Mining</span>
                    <span className="text-gray-700 dark:text-slate-300">{miner.nowMining}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Last seen</span>
                    <span className="tabular-nums text-gray-700 dark:text-slate-300">{formatTimeAgo(miner.lastSeen)}</span>
                  </div>
                  {miner.temperature && (
                    <div className="flex items-center justify-between">
                      <span>Temperature</span>
                      <span className="tabular-nums text-gray-700 dark:text-slate-300">{miner.temperature}°C</span>
                    </div>
                  )}
                  {miner.power && (
                    <div className="flex items-center justify-between">
                      <span>Power</span>
                      <span className="tabular-nums text-gray-700 dark:text-slate-300">{miner.power}W</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default MinersPanel ;
